import {
  type Application,
  type ApplicationListItem,
  type StageBlock,
  type StageData,
  OfferDisplay,
} from "@/api/http/schemas/applicationSchemas";
import { createGlobalState, useSessionStorage } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { ApplicationsHttpService } from "@/api/http/ApplicationsHttpService";
import { useReadonlyWithSetter } from "@/composables/common";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import { usePromiseWrapper } from "@/composables/common";
import useOptionsState from "./optionsState";
import { OfferStatus, type Offer, type GroupedOffer } from "@/api/http/schemas/offerSchemas";
import useWidgetModalState, { buildResumeWidgetUrl } from "@/composables/state/widgetModalState";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";
import { AnalyticsEvent } from "@/enums/analytics";
import { useAnalyticsState } from "./analyticsState";

const useApplicationsState = createGlobalState(() => {
  const { options } = useOptionsState();
  const { open: openWidgetModal } = useWidgetModalState();
  const [selectedApplication, setSelectedApplication] = useReadonlyWithSetter<Application | null>(
    useSessionStorage("selectedApplication", null, {
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => JSON.stringify(v),
      },
    })
  );

  /**
   * This is different from the selected application above. The active application is the latest (by created_at) application
   * submitted by the borrower. If the user logs in as an ephemeral user, meaning with a specific application ID,
   * like they do when we create a BP link from the application links page, then the active application will be that
   * single application, without looking at all other deals that might have been submitted by that user.
   * On the other hand, if the user logs in where they can see all of their deals, then the last application is the active one.
   */
  const [activeApplication, setActiveApplication] = useReadonlyWithSetter<Application | null>(null);

  const [allApplications, setAllApplications] = useReadonlyWithSetter<ApplicationListItem[]>([]);

  const getApplicationAndSetAsSelected = async (id: Application["id"]) => {
    const { showLoadingFullscreen } = usePlatformScreenState();
    showLoadingFullscreen.value = selectedApplication.value?.id !== id;
    const data = await new ApplicationsHttpService().getApplication(id, { include_content: true });
    showLoadingFullscreen.value = false;
    setSelectedApplication(data);
    const { trackEvent } = useAnalyticsState();
    trackEvent(AnalyticsEvent.ApplicationChanged, { application_id: data.id });
  };

  const refreshSelectedApplication = usePromiseWrapper(async () => {
    if (!selectedApplication.value) {
      return;
    }

    const data = await new ApplicationsHttpService().getApplication(selectedApplication.value.id);
    setSelectedApplication(data);
  });

  const getActiveApplication = async () => {
    const activeApplicationId = options.value?.active_application_id;

    if (!activeApplicationId) {
      return;
    }

    if (selectedApplication.value?.id === activeApplicationId) {
      setActiveApplication(selectedApplication.value);
      return;
    }

    const activeApplication = await new ApplicationsHttpService().getApplication(
      activeApplicationId
    );

    setActiveApplication(activeApplication);
  };

  const getApplications = async () => {
    const applicationsHttpService = new ApplicationsHttpService();
    const data = await applicationsHttpService.getApplicationsList();
    setAllApplications(data);
  };

  const currentStage = computed<StageData | undefined>(() => {
    return selectedApplication.value?.stages.find((stage) => stage.is_current);
  });

  const applicationStages = computed(() => selectedApplication.value?.stages ?? []);

  const isDeclined = computed(() => !!selectedApplication.value?.is_dead);

  const allOffers = computed(() => selectedApplication.value?.offers ?? []);

  const chatIsEnabled = computed(() => !!activeApplication.value?.chat?.is_enabled);

  const excludeOffers = ref<Offer["id"][]>([]);
  const dynamicOffers = ref<GroupedOffer[]>([]);
  const endStatus = [OfferStatus.Accepted, OfferStatus.Declined];

  const groupedOffers = computed(() => {
    const hasDynamic =
      selectedApplication.value?.offer_display === OfferDisplay.Dynamic &&
      (blocksAreActive(["all_offers"]) || isDeclined.value);

    if (!hasDynamic) {
      return {};
    }

    return allOffers.value.reduce<Record<string, Offer[]>>((acc, offer: Offer) => {
      const key = `${offer.funder_id}-${offer.offer_template_id}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(offer);

      return acc;
    }, {});
  });

  const acceptedOffers = computed(
    () =>
      selectedApplication.value?.offers.filter((offer) => offer.status === OfferStatus.Accepted) ??
      []
  );

  const fundedOffers = computed(() =>
    acceptedOffers.value.filter((offer) => !!offer.has_closing_details)
  );

  const offersToShow = computed(() => {
    if (!currentStage.value) {
      return [];
    }

    const offersToShow: Offer[] = [];

    if (blocksAreActive(["all_offers"]) || isDeclined.value) {
      offersToShow.push(...allOffers.value.filter(({ id }) => !excludeOffers.value.includes(id)));
    }

    if (blocksAreActive(["accepted_offer"])) {
      offersToShow.push(...acceptedOffers.value);
    }

    if (blocksAreActive(["funding"])) {
      offersToShow.push(...fundedOffers.value);
    }

    const offersOrders: {
      type: "dynamic" | "offer";
      weight: number;
      item: Offer | GroupedOffer;
    }[] = [];

    offersToShow.forEach((offer) => {
      offersOrders.push({
        type: "offer",
        weight: offerWeight(offer.funder_id, offer.offer_template_id),
        item: offer,
      });
    });

    if (blocksAreActive(["all_offers"]) || isDeclined.value) {
      dynamicOffers.value.forEach((group) => {
        offersOrders.push({
          type: "dynamic",
          weight: offerWeight(group.funder_id, group.offer_template_id),
          item: group,
        });
      });
    }

    return orderBy(offersOrders, "weight");
  });

  const hasAvailableOffers = computed(() => (selectedApplication.value?.offers ?? []).length > 0);

  const showOffersList = computed(
    () => hasAvailableOffers.value && blocksAreActive(["all_offers", "accepted_offer", "funding"])
  );

  const offerDeclineReasons = computed(() => {
    return {
      defined: selectedApplication.value?.offer_declined_reasons ?? [],
      other: selectedApplication.value?.offer_declined_reasons_other_label ?? "",
    };
  });

  const currentStageButtonConfig = computed(() => ({
    showButton: currentStage.value?.show_button ?? false,
    text: currentStage.value?.button_text ?? "",
    url: currentStage.value?.button_url ?? "",
    buttonRedirectType: currentStage.value?.button_redirect_type ?? "",
  }));

  const offerWeight = (funderId: string, offerTemplateId: string): number => {
    const DEFAULT_OFFER_TEMPLATE_WEIGHT = 1000;

    const funderSetting = selectedApplication.value?.funder_setting?.find(
      ({ id }) => id === funderId
    );
    if (!funderSetting) {
      return DEFAULT_OFFER_TEMPLATE_WEIGHT;
    }

    const offerTemplateSetting = funderSetting.offer_templates?.find(
      ({ id }) => id === offerTemplateId
    );

    return offerTemplateSetting ? offerTemplateSetting.weight : DEFAULT_OFFER_TEMPLATE_WEIGHT;
  };

  const blocksAreActive = (blocks: StageBlock[]): boolean => {
    const currentBlocks = currentStage.value?.blocks;

    if (!currentBlocks) {
      return false;
    }

    return currentBlocks.some((block) => blocks.includes(block));
  };

  const openCurrentStageButtonUrl = async () => {
    const url = currentStageButtonConfig.value.url;
    const redirectType = currentStageButtonConfig.value.buttonRedirectType;

    if (redirectType === "resume_link" && url) {
      openWidgetModal(buildResumeWidgetUrl(url));
      return;
    }

    const newTab = window.open("", "_blank");

    if (!url || !newTab) {
      newTab?.close();
      return;
    }

    newTab.location = url;
  };

  watch(
    () => groupedOffers.value,
    (offers) => {
      if (isEmpty(offers)) {
        return;
      }
      excludeOffers.value = [];
      dynamicOffers.value = [];
      Object.keys(offers).forEach((group) => {
        const groupOffer: Offer[] = offers[group];
        if (groupOffer.length > 1) {
          const hasDynamicFields = groupOffer[0].offer_details.some(({ dynamic }) => dynamic);
          if (!hasDynamicFields) {
            return;
          }
          const hasEndStatusOffer = groupOffer.some((offer) =>
            endStatus.includes(offer.status || OfferStatus.Sent)
          );
          if (hasEndStatusOffer) {
            const offersWithoutEndStatus = groupOffer.filter(
              (offer) => !endStatus.includes(offer.status || OfferStatus.Sent)
            );
            if (offersWithoutEndStatus.length) {
              offersWithoutEndStatus.forEach(({ id }) => excludeOffers.value.push(id));
            }
          } else {
            dynamicOffers.value.push({
              id: group,
              funder_id: groupOffer[0].funder_id,
              offer_template_id: groupOffer[0].offer_template_id,
              offers: groupOffer,
              weight: offerWeight(groupOffer[0].funder_id, groupOffer[0].offer_template_id),
            });
            groupOffer.forEach(({ id }) => excludeOffers.value.push(id));
          }
        }
      });
    }
  );

  return {
    activeApplication,
    getActiveApplication,
    selectedApplication,
    allApplications,
    getApplications,
    setSelectedApplication,
    setActiveApplication,
    setAllApplications,
    getApplicationAndSetAsSelected,
    refreshSelectedApplication,
    currentStage,
    applicationStages,
    isDeclined,
    allOffers,
    acceptedOffers,
    fundedOffers,
    offersToShow,
    hasAvailableOffers,
    offerDeclineReasons,
    currentStageButtonConfig,
    openCurrentStageButtonUrl,
    blocksAreActive,
    dynamicOffers,
    groupedOffers,
    excludeOffers,
    chatIsEnabled,
    showOffersList,
  };
});

export default useApplicationsState;
